import React, { useState, useMemo } from "react";
import { Modal, Button, Tag, FormInstance, Typography } from "antd";
import {
  FileOutlined,
  DownOutlined,
  CloseOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import Table from "../Table";

import styles from "./index.module.scss";
import {
  ReadingGetParams,
  ReadingTableRow,
  useReadingTableGet,
} from "../../../hooks/api/readings";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { useSearchParams } from "react-router";
import { getSorterParams } from "../../../utils";
import { LazySelect } from "../SelectLazyLoader";
import { useMiddahTableGet } from "../../../hooks/api/middot";
import { TYPE_COLORS } from "../../Pages/ContentLibrary/ReadingsTab";

interface SelectReadingsModalProps {
  open: boolean;
  onClose: () => void;
  onAdd?: (readings: ReadingTableRow[]) => void;
  readings?: ReadingTableRow[];
  form?: FormInstance;
}

export default function SelectReadingsModal({
  form,
  open,
  onClose,
  onAdd,
}: SelectReadingsModalProps): React.ReactElement {
  const [, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Храним полные данные выбранных readings
  const [selectedItems, setSelectedItems] = useState<
    Map<string, ReadingTableRow>
  >(new Map());
  const [searchText, setSearchText] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const readingTableGet = useReadingTableGet();
  const middahTableGet = useMiddahTableGet();

  console.log("Selected IDs:", Array.from(selectedIds));

  // Все выбранные readings из всех страниц
  const selectedReadings = useMemo(() => {
    return Array.from(selectedItems.values());
  }, [selectedItems]);

  const visibleSelected = showAllSelected
    ? selectedReadings
    : selectedReadings.slice(0, 4);

  const fetchMiddahOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await middahTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const mapMiddahOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  const handleSelectionChange = (
    selectedRowKeys: React.Key[],
    selectedRows: ReadingTableRow[],
  ) => {
    const currentPageIds = (readingTableGet.data?.data || []).map((r) => r.id);

    setSelectedIds((prevIds) => {
      const nextIds = new Set(prevIds);
      currentPageIds.forEach((id) => nextIds.delete(id));
      (selectedRowKeys as string[]).forEach((id) => nextIds.add(id));
      return nextIds;
    });

    setSelectedItems((prevItems) => {
      const nextItems = new Map(prevItems);
      currentPageIds.forEach((id) => nextItems.delete(id));
      selectedRows.forEach((row) => {
        nextItems.set(row.id, row);
      });
      return nextItems;
    });
  };

  const handleRemoveSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const handleClearAll = () => {
    setSelectedIds(new Set());
    setSelectedItems(new Map());
  };

  const handleAdd = () => {
    const readings = Array.from(selectedItems.values());

    form?.setFieldsValue({
      selectedReadings: readings.map((r) => ({
        id: r.id,
        name: r.name,
        type: r.type,
      })),
    });
    onAdd?.(readings);
    setSelectedIds(new Set());
    setSelectedItems(new Map());
    setShowAllSelected(false);
    onClose();
    setSearchParams({}, { replace: true });
    console.log("Form", form?.getFieldsValue());
  };

  const handleClose = () => {
    setSearchParams({}, { replace: true });
    setSelectedIds(new Set());
    setSelectedItems(new Map());
    setSearchText("");
    setSelectedTags([]);
    setShowAllSelected(false);
    onClose();
  };

  const columns = (): ProColumns<ReadingTableRow>[] => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      hideInSearch: true,
      render: (_: any, record: ReadingTableRow) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileOutlined style={{ color: "#999" }} />
          <span style={{ textDecoration: "underline" }}>{record.name}</span>
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      valueType: "select",
      sorter: true,
      hideInSearch: true,
      valueEnum: {
        pdf: { text: "PDF" },
        doc: { text: "DOC" },
      },
      render: (_, record) => (
        <Tag
          variant="outlined"
          color={TYPE_COLORS[record.type.toLowerCase()] || "default"}
          style={{ textTransform: "uppercase", fontSize: 12 }}
        >
          {record.type}
        </Tag>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      valueType: "select",
      hideInSearch: true,
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <Tag color="#722ED1" variant="outlined" key={tag.id}>
                {tag.name}
              </Tag>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "middahIds",
      key: "middahIds",
      valueType: "select",
      hideInTable: true,
      renderFormItem: () => (
        <LazySelect
          mode="multiple"
          fetchOptions={fetchMiddahOptions}
          mapOption={mapMiddahOption}
          pageSize={20}
        />
      ),
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ReadingTableRow>>> => {
    const newParams: ReadingGetParams = {
      page: current || 1,
      pageSize: pageSize || 10,
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return readingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <Modal
      title="Select Readings from Library"
      open={open}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnHidden
    >
      <div className={styles.container}>
        <Table<ReadingTableRow>
          key={`past-${tableKey}`}
          resetKey={() => setTableKey(Date.now())}
          className="tableCustomPadding"
          headerTitle="SelectUserModal"
          request={tableRequest}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: Array.from(selectedIds),
            preserveSelectedRowKeys: true,
            onChange: handleSelectionChange,
          }}
          columns={columns()}
          search={{
            style: {
              padding: "24px 0",
              border: "1px solid #0000000F",
              borderLeft: "none",
              borderRight: "none",
              borderRadius: 8,
            },
          }}
          options={{
            density: false,
            setting: false,
            reload: false,
            search: {
              name: "search",
              placeholder: "Search by reading name",
              style: { width: 280 },
            },
          }}
          columnsState={{
            persistenceKey: "pro-table-future-meetings",
            persistenceType: "localStorage",
          }}
          tableAlertRender={({ selectedRowKeys, onCleanSelected }) => {
            if (selectedIds.size === 0) return null;

            return (
              <div
                style={{
                  backgroundColor: "#E6F4FF",
                  border: "1px solid #1677FF",
                  borderRadius: 8,
                  padding: "16px 20px",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 12 }}
                >
                  <Typography.Text
                    strong
                    style={{
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      paddingTop: 4,
                      minWidth: "fit-content",
                    }}
                  >
                    {selectedIds.size} file
                    {selectedIds.size !== 1 ? "s" : ""} selected:
                  </Typography.Text>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px 8px",
                      flex: 1,
                      lineHeight: "28px",
                    }}
                  >
                    {visibleSelected.map((reading) => (
                      <Tag
                        key={reading.id}
                        closable
                        onClose={() => handleRemoveSelected(reading.id)}
                        style={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #00000026",
                          borderRadius: 4,
                          padding: "1px 8px",
                          fontSize: 14,
                          color: "#000000D9",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginRight: 0,
                          height: 28,
                        }}
                        closeIcon={
                          <CloseOutlined
                            style={{ fontSize: 10, color: "#000000E0" }}
                          />
                        }
                      >
                        <span
                          style={{
                            maxWidth: 280,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={reading.name}
                        >
                          {reading.name}
                        </span>
                      </Tag>
                    ))}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 16,
                    marginTop: 12,
                  }}
                >
                  {selectedReadings.length > 4 && (
                    <Button
                      type="primary"
                      ghost
                      icon={showAllSelected ? <UpOutlined /> : <DownOutlined />}
                      onClick={() => setShowAllSelected(!showAllSelected)}
                    >
                      Show {showAllSelected ? "less" : "more"}
                    </Button>
                  )}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      onCleanSelected();
                      handleClearAll();
                    }}
                    style={{ padding: 0, color: "#000000E0" }}
                  >
                    Clear all
                  </Button>
                </div>
              </div>
            );
          }}
          tableAlertOptionRender={false}
        />

        {/* Footer */}
        <div className={styles.footer}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleAdd}
            disabled={selectedIds.size === 0}
          >
            Add Readings ({selectedIds.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
